import React, { useState } from 'react';
import { styled } from '@mui/system';
import { Box, Button, Typography } from '@mui/material';
import { bgColors, colors, theme } from '../styles/globalStyles';
import LoginInput from '../components/inputs/loginInput';
import PasswordInput from '../components/inputs/passwordInput';
import Bg from '../images/bg.png';
import { ReactComponent as INTG101 } from '../images/intg101_logo.svg';
import { ReactComponent as SignIn } from '../images/sign_in.svg';
import { ReactComponent as StartTrackingArrows } from '../images/start_tracking_arrows.svg';
import { ReactComponent as ArrowLeftWhite } from '../images/arrowLeftWhite.svg';
import { ReactComponent as ArrowLeft } from '../images/left_arrow.svg';
import { useNavigate } from 'react-router-dom';
import { authTelegramUser, authUser, getUserData } from '../api/auth';
import { useEffect } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { userAtom } from '../recoil/atoms/userAtom';
import stories from './../data/stories.json';
import StoryModal from '../components/modals/storyModal';
import { getWatchedStoriesData } from '../api/stories';
import { Loader } from '../components/loaders/loader';
import { languagesAtom, selectedLanguageAtom } from '../recoil/atoms/languageAtom';
import data from '../data/data.json';
import { clearLocalStorage } from '../utils';
import Dropdown from '../components/dropdowns/dropdown';

const Layout = styled('div')(({ background }) => ({
    padding: 17,
    minHeight: '100svh',
    position: 'relative',

    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: '30px',

    background: `${background}, url(${Bg})`,
    backgroundSize: 'cover',
    backgroundBlendMode: 'multiply',
    borderRadius: 0,

    color: colors.white,

    [theme.breakpoints.up('xs')]: {
        maxWidth: 390,
        margin: '0 auto',
        borderRadius: 23,
    },
}));

const Header = styled('header')({
    paddingTop: 11,
    paddingLeft: 11,

    display: 'flex',
    alignItems: 'center',
    gap: 11,
});

const ContentWrapper = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
}));

const TypographySection = styled('section')({
    paddingInline: 10,
});

const TypographyMainOpaque = styled(Typography)({
    fontSize: 24,
    fontWeight: 400,
    opacity: 0.6,
    color: colors.white,
    marginBottom: 17,
});

const TypographyMain = styled(Typography)({
    fontSize: 58,
    fontWeight: 800,
    lineHeight: '110%',
    color: colors.white,
});

const TypographyComment = styled(Typography)({
    fontSize: 25,
    fontWeight: 500,
    lineHeight: '110%',
    color: colors.white,
});

export const RoundItems = styled(Box)(({ theme }) => ({}));

const RoundItem = styled('div')(({ theme, isStoryButton }) => ({
    padding: '17px 5px',
    width: '100%',
    border: '1px solid #FFFFFF33',
    background: 'rgba(255, 255, 255, 0.14)',
    borderRadius: 40,
    textAlign: 'center',
    textWrap: 'balance',
    cursor: isStoryButton ? 'pointer' : 'default',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,

    // [theme.breakpoints.up('sm')]: {
    //     padding: '72.5px 92.5px',
    // },
}));

const TypographyRoundItem = styled(Typography)({
    fontSize: 12,
    fontWeight: 700,
    lineHeight: '128.5%',
    color: colors.white,
});

const TypographyFooter1 = styled(Typography)({
    textAlign: 'center',

    fontSize: 15,
    fontWeight: 400,
    lineHeight: '150%',
    opacity: 0.6,
    color: colors.white,
});

const ErrorText = styled(Typography)({
    marginLeft: 10,
    fontSize: 15,
    fontWeight: 500,
    color: colors.red,
    lineHeight: '150%',
});

const TypographyFooter2 = styled('span')({
    marginLeft: 5,
    fontWeight: 700,
    color: colors.white,
});

const TypographyComment2 = styled(TypographyComment)({
    textAlign: 'center',
    marginTop: '10px',
    opacity: 0.9,
    fontSize: 14,
});

export const DropdownWrapper = styled('div')(({ theme }) => ({
    backgroundColor: 'white',
    borderRadius: 20,

    marginLeft: 'auto',
}));

const WelcomePage = () => {
    const [selectedLanguage, setSelectedLanguage] = useRecoilState(selectedLanguageAtom);
    const { welcomePage } = data[selectedLanguage];
    const translatedStories = stories[selectedLanguage];
    const [logInForm, setLogInForm] = useState(false);
    const isLogged = localStorage.getItem('auth-token') || localStorage.getItem('refresh-token') ? true : false;
    const [userName, setUserName] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState(null);
    const setUserInfo = useSetRecoilState(userAtom);
    const setLanguage = useSetRecoilState(selectedLanguageAtom);
    const [isStoryOpen, setIsStoryOpen] = useState(false);

    const languages = useRecoilValue(languagesAtom);
    const selectedLanguageIndex = languages.findIndex(({ code }) => selectedLanguage === code);

    const [hasWatchedStories, setHasWatchedStories] = useState(true);
    const [isTelegramAuthProcessing, setIsTelegramAuthProcessing] = useState(false);
    const storySetId = 1;

    let navigate = useNavigate();

    const bgColor = bgColors.purpleBlue;
    const color = colors.purple;

    const handleLanguageChange = ({ code }) => {
        setSelectedLanguage(code);
        localStorage.setItem('language', code);
    };

    useEffect(() => {
        const telegramId = window?.Telegram?.WebApp?.initDataUnsafe?.user?.id;

        if (!Boolean(telegramId)) {
            return;
        }

        getWatchedStoriesData(telegramId)
            .then((res) => {
                const watchedSets = res.data?.watched_story_sets;
                const isWatched = watchedSets.includes(storySetId);

                if (isWatched) {
                    return;
                }

                setHasWatchedStories(false);
            })
            .catch((err) => {
                console.error(err);
            });
    }, []);

    const checkIsLogged = () => {
        if (!isLogged) {
            const telegramId = window?.Telegram?.WebApp?.initDataUnsafe?.user?.id;

            if (!Boolean(telegramId)) {
                setLogInForm(true);
                return;
            }

            setIsTelegramAuthProcessing(true);
            authTelegramUser(telegramId)
                .then(({ data }) => {
                    localStorage.setItem('auth-token', data.access);
                    localStorage.setItem('refresh-token', data.refresh);
                    getUserData()
                        .then((user) => {
                            setUserInfo(user);
                            setLanguage(user.language);

                            localStorage.setItem('user-id', user.userId);
                            localStorage.setItem('team-id', user.teamId);
                            localStorage.setItem('username', user.username);
                            localStorage.setItem('language', user.language);

                            if (user.isLeader) {
                                navigate('/admin');
                                return;
                            }

                            navigate('/main');
                        })
                        .catch((error) => {
                            console.error('Ошибка получения пользователя', error);
                            setIsTelegramAuthProcessing(false);
                        });
                })
                .catch((err) => {
                    clearLocalStorage();
                    if (err.code === 'ERR_NETWORK' || err.code === 'ERR_BAD_RESPONSE') {
                        setIsTelegramAuthProcessing(false);
                        return;
                    }

                    if (window.Telegram.WebApp.initDataUnsafe.start_param) {
                        navigate('/quiz');
                        return;
                    }

                    const params = new URLSearchParams(document.location.search);
                    if (params.get('tgWebAppStartParam')) {
                        window.Telegram.WebApp.initDataUnsafe.start_param = params.get('tgWebAppStartParam');
                        navigate('/quiz');
                        return;
                    }

                    handleRegistration();
                });
        }

        getUserData()
            .then((user) => {
                setLanguage(user.language);
                localStorage.setItem('language', user.language);

                if (user.isLeader) {
                    navigate('/admin');
                    return;
                }
                navigate('/main');
            })
            .catch((error) => {
                console.error('Ошибка получения пользователя', error);
            });
    };

    const handleUserNameChange = (e) => {
        setUserName(e.target.value);
    };

    const handlePasswordChange = (e) => {
        setPassword(e.target.value);
    };

    const handleSignIn = (e) => {
        e.preventDefault();
        setError(null);
        authUser(userName, password, window.Telegram?.WebApp?.initDataUnsafe?.user?.id)
            .then(({ data }) => {
                localStorage.setItem('auth-token', data.access);
                localStorage.setItem('refresh-token', data.refresh);
                localStorage.removeItem('user-id-for-admin-panel');

                getUserData()
                    .then((user) => {
                        setUserInfo(user);
                        setLanguage(user.language);

                        localStorage.setItem('user-id', user.userId);
                        localStorage.setItem('team-id', user.teamId);
                        localStorage.setItem('username', user.username);
                        localStorage.setItem('language', user.language);

                        if (user.isLeader) {
                            navigate('/admin');
                            return;
                        }

                        navigate('/main');
                    })
                    .catch((error) => {
                        console.error('Ошибка получения пользователя', error);
                    });
            })
            .catch((err) => {
                console.error('Ошибка авторизации пользователя', err);
                if (err.response.data.detail === 'No active account found with the given credentials')
                    setError('Invalid username or password');
            });
    };

    const handleRegistration = (e) => {
        navigate('/registration');
    };

    return (
        <Layout background={bgColor}>
            {(!hasWatchedStories || isStoryOpen) && (
                <StoryModal
                    storiesGroup={translatedStories.find((group) => group.id === storySetId)}
                    isAutoOpen={true}
                    setIsStoryOpen={setIsStoryOpen}
                />
            )}
            <Header>
                {logInForm && (
                    <Button
                        startIcon={<ArrowLeft fill={color} />}
                        variant="light"
                        onClick={() => setLogInForm(false)}
                        sx={{ color }}
                    >
                        {welcomePage.signIn.backButton}
                    </Button>
                )}
                <INTG101 />

                <DropdownWrapper>
                    <Dropdown
                        variant="languageDropdown"
                        options={languages.map(({ title, code }) => ({ code, name: title }))}
                        setSelectedOption={handleLanguageChange}
                        selected={selectedLanguageIndex}
                        isDarkIcon={true}
                        isLangDropdown={true}
                    />
                </DropdownWrapper>
            </Header>
            {logInForm ? (
                <>
                    <TypographySection>
                        <TypographyMainOpaque>{welcomePage.signIn.opaque}</TypographyMainOpaque>
                        <TypographyMain>{welcomePage.signIn.main}</TypographyMain>
                    </TypographySection>
                    <ContentWrapper>
                        {error ? <ErrorText>{error}</ErrorText> : null}
                        <section>
                            <LoginInput value={userName} onChange={(e) => handleUserNameChange(e)} />
                            <PasswordInput value={password} onChange={(e) => handlePasswordChange(e)} />
                        </section>
                        <section>
                            <Button
                                variant="large"
                                startIcon={<SignIn fill={color} />}
                                sx={{ color, '&:hover': { backgroundColor: colors.white } }}
                                onClick={handleSignIn}
                                data-testid="loginBtn"
                            >
                                {welcomePage.signIn.loginButton}
                            </Button>
                            {window.Telegram?.WebApp?.initData && (
                                <>
                                    <TypographyComment2>{welcomePage.signIn.dividingWord}</TypographyComment2>
                                    <Button
                                        variant="large"
                                        startIcon={<SignIn fill={color} />}
                                        sx={{ color, '&:hover': { backgroundColor: colors.white }, marginTop: '10px' }}
                                        onClick={handleRegistration}
                                        data-testid="regBtn"
                                    >
                                        {welcomePage.signIn.signupButton}
                                    </Button>
                                </>
                            )}
                        </section>
                    </ContentWrapper>
                </>
            ) : (
                <>
                    <TypographySection>
                        <TypographyMainOpaque>{welcomePage.welcome.opaque}</TypographyMainOpaque>
                        <TypographyMain>{welcomePage.welcome.main}</TypographyMain>
                    </TypographySection>
                    <ContentWrapper>
                        <TypographySection>
                            <TypographyComment>{welcomePage.welcome.comment}</TypographyComment>
                        </TypographySection>
                        <RoundItems>
                            {welcomePage.roundItems.map(({ text, isStoryButton }) => (
                                <RoundItem
                                    key={text}
                                    as={isStoryButton ? 'button' : 'div'}
                                    isStoryButton={isStoryButton}
                                    onClick={isStoryButton ? () => setIsStoryOpen(true) : null}
                                >
                                    <TypographyRoundItem>{text}</TypographyRoundItem>
                                    {isStoryButton && <ArrowLeftWhite />}
                                </RoundItem>
                            ))}
                        </RoundItems>
                        <Button
                            variant="large"
                            endIcon={isTelegramAuthProcessing ? null : <StartTrackingArrows fill={color} />}
                            onClick={() => checkIsLogged()}
                            sx={{ color, '&:hover': { backgroundColor: colors.white } }}
                            data-testid="startBtn"
                        >
                            {isTelegramAuthProcessing ? (
                                <Loader size="medium" color={colors.main} />
                            ) : (
                                welcomePage.signIn.startButton
                            )}
                        </Button>
                        <TypographyFooter1>
                            {welcomePage.footer.text}
                            <TypographyFooter2>{welcomePage.footer.link}</TypographyFooter2>
                        </TypographyFooter1>
                    </ContentWrapper>
                </>
            )}
        </Layout>
    );
};

export default WelcomePage;
